import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import api from '@/lib/api';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, complete2fa } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code2fa, setCode2fa] = useState('');

  const goIn = (u) => {
    toast.success('Listo');
    const dest = loc.state?.from?.pathname || (u.role === 'superadmin' ? '/superadmin/dashboard' : '/app/dashboard');
    nav(dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (forgot) {
        await api.post('/auth/forgot-password', { email });
        setForgotSent(true);
        toast.success('Revisa tu correo');
        return;
      }
      if (challenge) {
        goIn(await complete2fa(challenge, code2fa));
        return;
      }
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setChallenge(result.challenge_token);
        return;
      }
      goIn(result);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1fr] bg-background">
      <div className="hidden lg:flex relative noise-bg text-white items-end p-14">
        <div className="relative z-10 max-w-md">
          <div className="brand-mark h-11 w-11 rounded-2xl text-base mb-10">Z</div>
          <h2 className="text-3xl font-semibold tracking-tight leading-[1.2]">
            CRM multi-empresa
          </h2>
          <p className="text-sm text-white/55 mt-3 leading-relaxed max-w-sm">
            WhatsApp, email y pipeline — datos aislados por tenant.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 page-canvas">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-[400px] surface-elevated p-8 sm:p-9"
        >
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="brand-mark h-9 w-9 rounded-xl text-sm">Z</div>
            <span className="font-semibold">Zunoid</span>
          </div>

          <h1 className="text-xl font-semibold tracking-tight">
            {challenge ? '2FA' : forgot ? 'Recuperar' : 'Entrar'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {challenge ? 'Código de tu app' : forgot ? 'Te enviamos un enlace' : 'Tu workspace'}
          </p>

          {error && (
            <div data-testid="login-error" className="mt-4 text-sm text-[hsl(var(--danger))]">
              {error}
            </div>
          )}
          {forgotSent && (
            <div className="mt-4 text-sm text-muted-foreground">
              Enlace enviado si el correo existe.
            </div>
          )}

          <div className="mt-6 space-y-3.5">
            {challenge ? (
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Código
                </label>
                <input
                  data-testid="login-2fa"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={8}
                  value={code2fa}
                  onChange={(e) => setCode2fa(e.target.value.replace(/\s/g, ''))}
                  className="input mt-1.5 text-center tracking-[0.3em] font-mono"
                  placeholder="000000"
                />
                <button
                  type="button"
                  className="text-xs text-muted-foreground mt-2 hover:text-foreground"
                  onClick={() => { setChallenge(null); setCode2fa(''); }}
                >
                  Volver
                </button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs text-muted-foreground">Email</label>
                  <input
                    data-testid="login-email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input mt-1.5"
                    placeholder="tu@empresa.com"
                  />
                </div>
                {!forgot && (
                  <div>
                    <label className="text-xs text-muted-foreground">Contraseña</label>
                    <div className="relative mt-1.5">
                      <input
                        data-testid="login-password"
                        type={show ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShow((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1"
                      >
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <button data-testid="login-submit" disabled={loading} className="btn-primary w-full mt-6 py-2.5">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {challenge ? 'Verificar' : forgot ? 'Enviar' : 'Entrar'}
          </button>

          {!challenge && (
            <button
              type="button"
              className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => { setForgot((v) => !v); setError(''); setForgotSent(false); }}
            >
              {forgot ? 'Volver' : 'Olvidé mi contraseña'}
            </button>
          )}

          <div className="mt-8 pt-5 border-t border-border text-center text-[11px] text-muted-foreground">
            <Link to="/" className="hover:text-foreground">zunoid.com.mx</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
